import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {
    adsErrorMessage,
    getCampaignReport,
    getCityDailyBreakdown,
    isCanceled,
    normalizeLevelRows,
} from '../../services/metaAdsService';
import { formatDayLabel, formatMetric, formatMoney, formatNumber, localDate } from './format';
import { downloadCampaignSpendTableXlsx } from './exportCampaignSpendTable';
import MetaAdsCampaignLoader from './MetaAdsCampaignLoader';
import MetaAdsLeadDialog from './MetaAdsLeadDialog';

const METRICS_PER_DAY = 7;
const STICKY_LEAD_WIDTH = 52;
const STICKY_CAMPAIGN_WIDTH = 280;
const STICKY_TOTAL_WIDTH = STICKY_LEAD_WIDTH + STICKY_CAMPAIGN_WIDTH;
/** Height of the first header row (date group labels); second row sticks below this. */
const HEADER_ROW1_HEIGHT_PX = 48;

const BUCKET_LABELS = {
    sql: 'SQL-Real',
    tbc: 'Sales-TBC',
    won: 'Sales Won',
    real: 'Total Real',
    received: 'Leads received',
};

function groupCityTags(row) {
    if (row.adsetCities?.length) return row.adsetCities;
    if (row.cities?.length) return row.cities;
    return [];
}

function matchesCityScope(row, cityName) {
    const tags = groupCityTags(row);
    if (!cityName || cityName === 'Untagged') {
        return tags.length === 0;
    }
    if (!tags.length) return false;
    return tags.includes(cityName);
}

function sumRowMetrics(rows) {
    const totals = {
        spend: 0,
        impressions: 0,
        clicks: 0,
        leadCount: 0,
        sqlReal: 0,
        salesTbc: 0,
        totalReal: 0,
        salesWon: 0,
        salesWonAmount: 0,
    };
    let currency = 'INR';
    rows.forEach((row) => {
        totals.spend += row.spend || 0;
        totals.impressions += row.impressions || 0;
        totals.clicks += row.clicks || 0;
        totals.leadCount += row.leadCount || 0;
        totals.sqlReal += row.sqlReal || 0;
        totals.salesTbc += row.salesTbc || 0;
        totals.totalReal += row.totalReal || 0;
        totals.salesWon += row.salesWon || 0;
        totals.salesWonAmount += row.salesWonAmount || 0;
        if (row.currency) currency = row.currency;
    });
    return {
        ...totals,
        currency,
        costPerClick: totals.clicks > 0 ? totals.spend / totals.clicks : null,
        costPerLead: totals.totalReal > 0 ? totals.spend / totals.totalReal : null,
    };
}

function campaignSpansMultipleCities(campaign) {
    const tags = groupCityTags(campaign);
    return tags.length > 1;
}

/** Under a city accordion, campaign spend/leads must match the sum of that city's ad sets only. */
function metricsForCampaignInCity(campaign, cityName, adsetsDetail) {
    const adsetRows = adsetsDetail?.rows?.filter((adset) => matchesCityScope(adset, cityName)) || [];
    if (adsetsDetail && !adsetsDetail.loading) {
        return { ...campaign, ...sumRowMetrics(adsetRows) };
    }
    if (adsetsDetail?.loading) {
        return { ...campaign, ...sumRowMetrics([]), metricsPending: true };
    }
    if (!campaignSpansMultipleCities(campaign) && matchesCityScope(campaign, cityName)) {
        return campaign;
    }
    return { ...campaign, ...sumRowMetrics([]) };
}

function campaignRowsForCity(campaigns, cityName, adsetsState) {
    return campaigns
        .filter((campaign) => matchesCityScope(campaign, cityName))
        .map((campaign) => metricsForCampaignInCity(
            campaign,
            cityName,
            adsetsState[campaign.campaignId],
        ));
}

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    heading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '18px 20px 0',
    },
    title: {
        margin: 0,
        fontSize: 18,
        fontWeight: 700,
        color: '#1C375D',
    },
    count: {
        color: '#8a92a3',
        fontSize: 14,
    },
    exportBtn: {
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: '#1C375D',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#152a47',
        },
    },
    scrollHint: {
        margin: '0 20px 8px',
        fontSize: 12,
        color: '#6b7a90',
    },
    tableScroll: {
        maxHeight: 'calc(100vh - 340px)',
        overflowX: 'auto',
        overflowY: 'auto',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
        padding: '0 0 16px',
        position: 'relative',
        isolation: 'isolate',
    },
    table: {
        width: 'max-content',
        minWidth: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        tableLayout: 'auto',
        textAlign: 'left',
    },
    headCell: {
        padding: '14px 12px',
        fontWeight: 700,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        whiteSpace: 'nowrap',
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        boxShadow: '0 2px 4px rgba(16, 24, 40, 0.12)',
    },
    headCellStickyRow1: {
        position: 'sticky',
        top: 0,
        zIndex: 12,
    },
    headCellStickyRow2: {
        position: 'sticky',
        top: HEADER_ROW1_HEIGHT_PX,
        zIndex: 11,
    },
    stickyLead: {
        position: 'sticky',
        left: 0,
        zIndex: 24,
        backgroundColor: '#1C375D',
        boxShadow: '2px 0 4px rgba(16, 24, 40, 0.08)',
    },
    stickyLeadBody: {
        position: 'sticky',
        left: 0,
        zIndex: 5,
        backgroundColor: '#fff',
        boxShadow: '2px 0 4px rgba(16, 24, 40, 0.06)',
    },
    stickyNameHead: {
        position: 'sticky',
        left: STICKY_LEAD_WIDTH,
        zIndex: 23,
        minWidth: STICKY_CAMPAIGN_WIDTH,
        width: STICKY_CAMPAIGN_WIDTH,
        maxWidth: STICKY_CAMPAIGN_WIDTH,
        backgroundColor: '#1C375D',
        boxShadow: '4px 0 8px rgba(16, 24, 40, 0.12)',
    },
    stickyNameBody: {
        position: 'sticky',
        left: STICKY_LEAD_WIDTH,
        zIndex: 5,
        minWidth: STICKY_CAMPAIGN_WIDTH,
        width: STICKY_CAMPAIGN_WIDTH,
        maxWidth: STICKY_CAMPAIGN_WIDTH,
        backgroundColor: '#fff',
        boxShadow: '4px 0 8px rgba(16, 24, 40, 0.08)',
        verticalAlign: 'top',
    },
    jumpTodayBtn: {
        marginLeft: 8,
        padding: 0,
        border: 'none',
        background: 'none',
        color: '#1C375D',
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    datePageHead: {
        textAlign: 'center',
        borderLeft: '3px solid rgba(255, 242, 235, 0.35)',
        textTransform: 'none',
        letterSpacing: 0,
        fontSize: 13,
    },
    datePageHeadToday: {
        backgroundColor: '#163049',
        borderLeftColor: '#F47E46',
    },
    dateSubHead: {
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        borderLeft: '1px solid rgba(255, 242, 235, 0.2)',
    },
    datePageCell: {
        borderLeft: '1px solid #eef0f3',
        backgroundColor: '#fcfdfe',
        minWidth: 72,
    },
    datePageStart: {
        borderLeft: '3px solid #d5dbe3',
    },
    datePageStartToday: {
        borderLeftColor: '#F47E46',
        backgroundColor: '#fffaf6',
    },
    dateSectionStart: {
        borderLeft: '3px solid #d5dbe3',
    },
    datePageHeadSectionStart: {
        borderLeft: '3px solid rgba(255, 242, 235, 0.35)',
    },
    numHead: {
        textAlign: 'right',
    },
    headStack: {
        whiteSpace: 'normal',
        lineHeight: 1.25,
        textTransform: 'none',
        fontSize: 11,
        letterSpacing: 0.2,
        verticalAlign: 'bottom',
    },
    bodyCell: {
        padding: '12px',
        fontSize: 13.5,
        borderBottom: '1px solid #eef0f3',
        verticalAlign: 'middle',
    },
    numCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
    },
    name: {
        display: 'block',
        fontWeight: 700,
        color: '#1C375D',
        lineHeight: 1.35,
        wordBreak: 'normal',
        overflowWrap: 'break-word',
    },
    id: {
        display: 'block',
        color: '#8a92a3',
        marginTop: 4,
        overflowWrap: 'anywhere',
        fontSize: 12,
        lineHeight: 1.3,
    },
    tag: {
        display: 'inline-block',
        marginBottom: 4,
        marginRight: 4,
        padding: '1px 6px',
        borderRadius: 4,
        backgroundColor: '#e7eef8',
        color: '#1C375D',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
    },
    cityTag: {
        display: 'inline-block',
        marginBottom: 4,
        marginRight: 4,
        padding: '1px 6px',
        borderRadius: 4,
        backgroundColor: '#e8f6ef',
        color: '#176b35',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
    },
    adTag: {
        backgroundColor: '#fff1ea',
        color: '#9a4b24',
    },
    empty: {
        padding: '28px 20px',
        color: '#8a92a3',
        fontSize: 14,
    },
    detailError: {
        color: '#a61b1b',
        fontSize: 13,
        padding: '12px 16px',
    },
    rowAlt: {
        backgroundColor: '#fafbfc',
        '& $stickyLeadBody, & $stickyNameBody': {
            backgroundColor: '#fafbfc',
        },
    },
    adsetRow: {
        backgroundColor: '#f4f7fb',
        '& $stickyLeadBody, & $stickyNameBody': {
            backgroundColor: '#f4f7fb',
        },
    },
    adRow: {
        backgroundColor: '#fbf8f6',
        '& $stickyLeadBody, & $stickyNameBody': {
            backgroundColor: '#fbf8f6',
        },
    },
    statusRow: {
        backgroundColor: '#f7f9fc',
    },
    targetInput: {
        width: 76,
        boxSizing: 'border-box',
        textAlign: 'right',
        border: '1px solid #d5dbe3',
        borderRadius: 6,
        padding: '6px 8px',
        fontSize: 13,
        color: '#1C375D',
        fontVariantNumeric: 'tabular-nums',
        backgroundColor: '#fff',
        '&:focus': {
            outline: '2px solid #1C375D33',
            borderColor: '#1C375D',
        },
    },
    met: {
        color: '#176b35',
        fontWeight: 700,
    },
    mid: {
        color: '#8a6a12',
        fontWeight: 700,
    },
    low: {
        color: '#a61b1b',
        fontWeight: 700,
    },
    totalRow: {
        backgroundColor: '#fff8f3',
        '& td': {
            fontWeight: 700,
            color: '#1C375D',
            borderTop: '2px solid #F47E46',
            borderBottom: 'none',
        },
        '& $stickyLeadBody, & $stickyNameBody': {
            backgroundColor: '#fff8f3',
        },
    },
    cityTotalRow: {
        backgroundColor: '#eef3f8',
        cursor: 'pointer',
        '& td': {
            fontWeight: 700,
            color: '#1C375D',
            borderBottom: '2px solid #d5dbe3',
        },
        '& $stickyLeadBody, & $stickyNameBody': {
            backgroundColor: '#eef3f8',
        },
        '&:hover': {
            backgroundColor: '#e4ebf3',
        },
        '&:hover $stickyLeadBody, &:hover $stickyNameBody': {
            backgroundColor: '#e4ebf3',
        },
    },
    leadLink: {
        border: 'none',
        padding: 0,
        margin: 0,
        background: 'none',
        color: '#1C375D',
        fontWeight: 700,
        fontSize: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': {
            color: '#0f2844',
        },
    },
}));

function achievedPercent(totalReal, target) {
    if (target == null || target <= 0 || totalReal == null) return null;
    return Math.round((totalReal / target) * 100);
}

function percentClass(classes, percent) {
    if (percent == null) return '';
    if (percent >= 100) return classes.met;
    if (percent >= 60) return classes.mid;
    return classes.low;
}

function emptyDayMetrics() {
    return {
        spend: 0,
        leadCount: 0,
        sqlReal: 0,
        salesTbc: 0,
        totalReal: 0,
        salesWon: 0,
        salesWonAmount: 0,
        currency: 'INR',
    };
}

function cityDailyKey(cityName) {
    if (cityName === 'Untagged') return '__untagged';
    return cityName;
}

function cityDayMetrics(cityDaily, cityName, date) {
    const key = cityDailyKey(cityName);
    return cityDaily?.cities?.[key]?.[date] || emptyDayMetrics();
}

function sumSalesWonForDates(dates, getDayMetrics) {
    return (dates || []).reduce((acc, date) => {
        const day = getDayMetrics ? getDayMetrics(date) : emptyDayMetrics();
        acc.salesWon += day.salesWon || 0;
        acc.salesWonAmount += day.salesWonAmount || 0;
        return acc;
    }, { salesWon: 0, salesWonAmount: 0 });
}

function sumCitySalesWon(cityDaily, cityName, dates) {
    if (!cityDaily?.cities || !dates?.length) {
        return { salesWon: 0, salesWonAmount: 0 };
    }
    return sumSalesWonForDates(dates, (date) => cityDayMetrics(cityDaily, cityName, date));
}

function totalSalesWonFromCityDaily(cityDaily, groups, dates, campaigns) {
    if (!cityDaily?.cities || !dates?.length) {
        return campaigns.reduce((acc, campaign) => ({
            salesWon: acc.salesWon + (campaign.salesWon || 0),
            salesWonAmount: acc.salesWonAmount + (campaign.salesWonAmount || 0),
        }), { salesWon: 0, salesWonAmount: 0 });
    }
    return groups.reduce((acc, [cityName]) => {
        const part = sumCitySalesWon(cityDaily, cityName, dates);
        acc.salesWon += part.salesWon;
        acc.salesWonAmount += part.salesWonAmount;
        return acc;
    }, { salesWon: 0, salesWonAmount: 0 });
}

function entityDayMetrics(cityDaily, store, entityId, date) {
    if (!entityId) return emptyDayMetrics();
    return cityDaily?.[store]?.[entityId]?.[date] || emptyDayMetrics();
}

function sumDayMetrics(days) {
    const totals = emptyDayMetrics();
    (days || []).forEach((day) => {
        if (!day) return;
        totals.spend += day.spend || 0;
        totals.leadCount += day.leadCount || 0;
        totals.sqlReal += day.sqlReal || 0;
        totals.salesTbc += day.salesTbc || 0;
        totals.totalReal += day.totalReal || 0;
        totals.salesWon += day.salesWon || 0;
        totals.salesWonAmount += day.salesWonAmount || 0;
        if (day.currency) totals.currency = day.currency;
    });
    if (!totals.totalReal && (totals.sqlReal || totals.salesTbc)) {
        totals.totalReal = totals.sqlReal + totals.salesTbc;
    }
    return totals;
}

function campaignDayMetricsInCity(cityDaily, campaign, cityName, adsetsDetail, date) {
    const adsetRows = adsetsDetail?.rows?.filter((adset) => matchesCityScope(adset, cityName)) || [];
    if (adsetsDetail && !adsetsDetail.loading && adsetRows.length) {
        return sumDayMetrics(adsetRows.map((adset) => entityDayMetrics(cityDaily, 'adsets', adset.id, date)));
    }
    return entityDayMetrics(cityDaily, 'campaigns', campaign.campaignId, date);
}

function DayPageCells({
    classes,
    day,
    isToday,
    isFirstDateColumn,
    date,
    leadScope,
    contextLabel,
    onOpenLeads,
}) {
    const sectionStart = isFirstDateColumn ? ` ${classes.dateSectionStart}` : '';
    const start = isToday
        ? `${classes.datePageCell} ${classes.datePageStart} ${classes.datePageStartToday}${sectionStart}`
        : `${classes.datePageCell} ${classes.datePageStart}${sectionStart}`;
    const cell = `${classes.bodyCell} ${classes.numCell}`;
    const currency = day.currency || 'INR';
    const totalReal = day.totalReal != null ? day.totalReal : day.leadCount;
    const dayContext = date && contextLabel
        ? `${contextLabel} · ${formatDayLabel(date)}`
        : contextLabel;
    return (
        <>
            <td className={`${start} ${cell}`}>{formatMoney(day.spend, currency)}</td>
            <LeadCountCell
                classes={classes}
                cellClass={`${classes.datePageCell} ${cell}`}
                count={day.leadCount || 0}
                bucket="received"
                scope={leadScope}
                contextLabel={dayContext}
                onOpenLeads={onOpenLeads}
                dayDate={date}
            />
            <LeadCountCell
                classes={classes}
                cellClass={`${classes.datePageCell} ${cell}`}
                count={day.sqlReal || 0}
                bucket="sql"
                scope={leadScope}
                contextLabel={dayContext}
                onOpenLeads={onOpenLeads}
                dayDate={date}
            />
            <LeadCountCell
                classes={classes}
                cellClass={`${classes.datePageCell} ${cell}`}
                count={day.salesTbc || 0}
                bucket="tbc"
                scope={leadScope}
                contextLabel={dayContext}
                onOpenLeads={onOpenLeads}
                dayDate={date}
            />
            <LeadCountCell
                classes={classes}
                cellClass={`${classes.datePageCell} ${cell}`}
                count={totalReal || 0}
                bucket="real"
                scope={leadScope}
                contextLabel={dayContext}
                onOpenLeads={onOpenLeads}
                dayDate={date}
            />
            <LeadCountCell
                classes={classes}
                cellClass={`${classes.datePageCell} ${cell}`}
                count={day.salesWon || 0}
                bucket="won"
                scope={leadScope}
                contextLabel={dayContext}
                onOpenLeads={onOpenLeads}
                dayDate={date}
            />
            <td className={`${classes.datePageCell} ${cell}`}>
                {formatMetric(
                    totalReal > 0 && day.spend ? day.spend / totalReal : null,
                    (value) => formatMoney(value, currency),
                )}
            </td>
        </>
    );
}

function sumDayAcrossCities(cityDaily, date) {
    const totals = emptyDayMetrics();
    const cities = cityDaily?.cities || {};
    Object.values(cities).forEach((byDay) => {
        const day = byDay[date];
        if (!day) return;
        totals.spend += day.spend || 0;
        totals.leadCount += day.leadCount || 0;
        totals.sqlReal += day.sqlReal || 0;
        totals.salesTbc += day.salesTbc || 0;
        totals.totalReal += day.totalReal || 0;
        totals.salesWon += day.salesWon || 0;
        totals.salesWonAmount += day.salesWonAmount || 0;
        if (day.currency) totals.currency = day.currency;
    });
    return totals;
}

function LeadCountCell({
    classes,
    cellClass,
    count,
    bucket,
    scope,
    contextLabel,
    onOpenLeads,
    dayDate,
}) {
    const cell = cellClass || `${classes.bodyCell} ${classes.numCell}`;
    const value = count || 0;
    if (!value || !onOpenLeads || !scope) {
        return <td className={cell}>{formatNumber(value)}</td>;
    }
    return (
        <td className={cell}>
            <button
                type="button"
                className={classes.leadLink}
                onClick={(event) => {
                    event.stopPropagation();
                    onOpenLeads(bucket, scope, contextLabel, dayDate);
                }}
            >
                {formatNumber(value)}
            </button>
        </td>
    );
}

function MetricCells({
    row,
    classes,
    leadScope,
    contextLabel,
    onOpenLeads,
    dates,
    today,
    getDayMetrics,
    periodSalesWon,
}) {
    const cell = `${classes.bodyCell} ${classes.numCell}`;
    const totalReal = row.totalReal != null ? row.totalReal : row.leadCount;
    const won = periodSalesWon || sumSalesWonForDates(dates, getDayMetrics);
    return (
        <>
            <td className={cell}>{formatMoney(row.spend, row.currency)}</td>
            <td className={cell}>{formatNumber(row.impressions)}</td>
            <td className={cell}>{formatNumber(row.clicks)}</td>
            <td className={cell}>
                {formatMetric(row.costPerClick, (value) => formatMoney(value, row.currency))}
            </td>
            <td className={cell}>—</td>
            <LeadCountCell
                classes={classes}
                count={row.leadCount || 0}
                bucket="received"
                scope={leadScope}
                contextLabel={contextLabel}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                count={row.sqlReal || 0}
                bucket="sql"
                scope={leadScope}
                contextLabel={contextLabel}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                count={row.salesTbc || 0}
                bucket="tbc"
                scope={leadScope}
                contextLabel={contextLabel}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                count={totalReal || 0}
                bucket="real"
                scope={leadScope}
                contextLabel={contextLabel}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                count={won.salesWon || 0}
                bucket="won"
                scope={leadScope}
                contextLabel={contextLabel}
                onOpenLeads={onOpenLeads}
            />
            <td className={cell}>{formatMoney(won.salesWonAmount || 0, row.currency)}</td>
            <td className={cell}>—</td>
            <td className={cell}>
                {formatMetric(row.costPerLead, (value) => formatMoney(value, row.currency))}
            </td>
            {dates?.map((date, dateIndex) => (
                <DayPageCells
                    key={date}
                    classes={classes}
                    day={getDayMetrics ? getDayMetrics(date) : emptyDayMetrics()}
                    isToday={date === today}
                    isFirstDateColumn={dateIndex === 0}
                    date={date}
                    leadScope={leadScope}
                    contextLabel={contextLabel}
                    onOpenLeads={onOpenLeads}
                />
            ))}
        </>
    );
}

function TargetCell({ city, savedTarget, classes, onSave }) {
    const saved = savedTarget == null ? '' : String(savedTarget);
    const [value, setValue] = React.useState(saved);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        setValue(saved);
    }, [saved]);

    async function commit() {
        if (saving || value === saved) return;
        const next = value === '' ? null : Number(value);
        setSaving(true);
        try {
            await onSave(city, next);
        } catch (error) {
            setValue(saved);
        } finally {
            setSaving(false);
        }
    }

    return (
        <input
            className={classes.targetInput}
            inputMode="numeric"
            aria-label={`Lead target for ${city}`}
            value={value}
            disabled={saving}
            placeholder="Set"
            onChange={(event) => setValue(event.target.value.replace(/[^\d]/g, '').slice(0, 7))}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
            }}
            onClick={(event) => event.stopPropagation()}
        />
    );
}

function costPerReal(spend, real, currency) {
    if (!real) return '—';
    return formatMoney(spend / real, currency);
}

function CityTotalRow({
    classes,
    cityName,
    rows,
    savedTarget,
    onSaveTarget,
    open,
    onToggle,
    onOpenLeads,
    dates,
    cityDaily,
    today,
    reportCampaignIds,
}) {
    const cityLeadScope = {
        city: cityName,
        ...(reportCampaignIds ? { campaignIds: reportCampaignIds } : {}),
    };
    const received = rows.reduce((sum, row) => sum + (row.leadCount || 0), 0);
    const sql = rows.reduce((sum, row) => sum + (row.sqlReal || 0), 0);
    const tbc = rows.reduce((sum, row) => sum + (row.salesTbc || 0), 0);
    const real = rows.reduce((sum, row) => sum + (row.totalReal || 0), 0);
    const { salesWon, salesWonAmount } = sumCitySalesWon(cityDaily, cityName, dates);
    const spend = rows.reduce((sum, row) => sum + (row.spend || 0), 0);
    const impressions = rows.reduce((sum, row) => sum + (row.impressions || 0), 0);
    const clicks = rows.reduce((sum, row) => sum + (row.clicks || 0), 0);
    const currency = rows.find((row) => row.currency)?.currency || 'INR';
    const cpc = clicks > 0 ? spend / clicks : null;
    const percent = achievedPercent(real, savedTarget);
    const numCell = `${classes.bodyCell} ${classes.numCell}`;

    function handleRowKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
        }
    }

    return (
        <tr
            className={classes.cityTotalRow}
            onClick={onToggle}
            onKeyDown={handleRowKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={open}
        >
            <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`}>
                <IconButton
                    size="small"
                    aria-hidden
                    tabIndex={-1}
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggle();
                    }}
                >
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </td>
            <td className={`${classes.bodyCell} ${classes.stickyNameBody}`}>
                <span className={classes.name}>{cityName}</span>
                <small className={classes.id}>
                    {rows.length}
                    {' '}
                    campaign
                    {rows.length === 1 ? '' : 's'}
                </small>
            </td>
            <td className={numCell}>{formatMoney(spend, currency)}</td>
            <td className={numCell}>{formatNumber(impressions)}</td>
            <td className={numCell}>{formatNumber(clicks)}</td>
            <td className={numCell}>
                {formatMetric(cpc, (value) => formatMoney(value, currency))}
            </td>
            <td className={numCell} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                <TargetCell
                    city={cityName}
                    savedTarget={savedTarget}
                    classes={classes}
                    onSave={onSaveTarget}
                />
            </td>
            <LeadCountCell
                classes={classes}
                cellClass={numCell}
                count={received}
                bucket="received"
                scope={cityLeadScope}
                contextLabel={cityName}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                cellClass={numCell}
                count={sql}
                bucket="sql"
                scope={cityLeadScope}
                contextLabel={cityName}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                cellClass={numCell}
                count={tbc}
                bucket="tbc"
                scope={cityLeadScope}
                contextLabel={cityName}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                cellClass={numCell}
                count={real}
                bucket="real"
                scope={cityLeadScope}
                contextLabel={cityName}
                onOpenLeads={onOpenLeads}
            />
            <LeadCountCell
                classes={classes}
                cellClass={numCell}
                count={salesWon}
                bucket="won"
                scope={cityLeadScope}
                contextLabel={cityName}
                onOpenLeads={onOpenLeads}
            />
            <td className={numCell}>{formatMoney(salesWonAmount, currency)}</td>
            <td className={`${numCell} ${percentClass(classes, percent)}`}>
                {percent == null ? '—' : `${percent}%`}
            </td>
            <td className={numCell}>{costPerReal(spend, real, currency)}</td>
            {dates?.map((date, dateIndex) => (
                <DayPageCells
                    key={`${cityName}-${date}`}
                    classes={classes}
                    day={cityDayMetrics(cityDaily, cityName, date)}
                    isToday={date === today}
                    isFirstDateColumn={dateIndex === 0}
                    date={date}
                    leadScope={cityLeadScope}
                    contextLabel={cityName}
                    onOpenLeads={onOpenLeads}
                />
            ))}
        </tr>
    );
}

function NameCell({ classes, label, tagClass, name, id, cities, sticky }) {
    return (
        <td className={`${classes.bodyCell} ${sticky ? classes.stickyNameBody : ''}`}>
            {label ? <span className={`${classes.tag} ${tagClass || ''}`}>{label}</span> : null}
            {cities?.map((city) => (
                <span className={classes.cityTag} key={city}>{city}</span>
            ))}
            <span className={classes.name}>{name}</span>
            <small className={classes.id}>{id}</small>
        </td>
    );
}

export default function CampaignSpendTable({
    campaigns,
    loading,
    syncing,
    from,
    to,
    hasError,
    emptyMessage,
    scopeLabel,
    groupCity,
    targets,
    onSaveTarget,
    uniqueTotals,
    metaPlatform = 'both',
    cityDailyData,
    cityDailyLoading = false,
}) {
    const classes = useStyles();
    const [openCities, setOpenCities] = useState({});
    const [openCampaigns, setOpenCampaigns] = useState({});
    const [adsets, setAdsets] = useState({});
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [openAdsets, setOpenAdsets] = useState({});
    const [ads, setAds] = useState({});
    const [leadDialog, setLeadDialog] = useState({ open: false, title: '', query: null });
    const managesCityDaily = cityDailyData !== undefined;
    const [cityDaily, setCityDaily] = useState(managesCityDaily ? cityDailyData : null);
    const [dailyLoading, setDailyLoading] = useState(managesCityDaily ? cityDailyLoading : false);
    const scrollRef = useRef(null);
    const today = localDate();
    const dates = React.useMemo(() => {
        const list = cityDaily?.dates?.length ? [...cityDaily.dates] : [];
        return list.sort((a, b) => String(b).localeCompare(String(a)));
    }, [cityDaily?.dates]);
    const periodColumnCount = 15;
    const columnCount = periodColumnCount + dates.length * METRICS_PER_DAY;
    const cityTargets = targets || {};
    const groups = React.useMemo(() => {
        if (groupCity) return [[groupCity, campaigns]];
        const grouped = new Map();
        campaigns.forEach((campaign) => {
            const scoped = groupCityTags(campaign);
            const tags = scoped.length ? scoped : ['Untagged'];
            tags.forEach((tag) => {
                if (!grouped.has(tag)) grouped.set(tag, []);
                grouped.get(tag).push(campaign);
            });
        });
        return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [campaigns, groupCity]);
    const targetTotal = groups.reduce((sum, [name]) => (
        cityTargets[name] == null ? sum : sum + cityTargets[name]
    ), 0);
    const targetsSet = groups.filter(([name]) => cityTargets[name] != null);
    const sqlSummed = campaigns.reduce((sum, campaign) => sum + (campaign.sqlReal || 0), 0);
    const tbcSummed = campaigns.reduce((sum, campaign) => sum + (campaign.salesTbc || 0), 0);
    const achievedSummed = campaigns.reduce((sum, campaign) => sum + (campaign.totalReal || 0), 0);
    const sqlTotal = uniqueTotals?.sqlReal != null ? uniqueTotals.sqlReal : sqlSummed;
    const tbcTotal = uniqueTotals?.salesTbc != null ? uniqueTotals.salesTbc : tbcSummed;
    const achievedTotal = uniqueTotals?.totalReal != null ? uniqueTotals.totalReal : achievedSummed;
    const salesWonGrand = totalSalesWonFromCityDaily(cityDaily, groups, dates, campaigns);
    const salesWonTotal = salesWonGrand.salesWon;
    const salesWonAmountTotal = salesWonGrand.salesWonAmount;
    const leadsReceivedTotal = campaigns.reduce((sum, campaign) => sum + (campaign.leadCount || 0), 0);
    const spendTotal = React.useMemo(() => {
        const fromCampaigns = campaigns.reduce((sum, campaign) => sum + (campaign.spend || 0), 0);
        if (!cityDaily?.cities || !dates.length) return fromCampaigns;
        const fromDaily = dates.reduce(
            (sum, date) => sum + (sumDayAcrossCities(cityDaily, date).spend || 0),
            0,
        );
        if (!groupCity && fromDaily + 1 < fromCampaigns) return fromCampaigns;
        return fromDaily;
    }, [cityDaily, dates, campaigns, groupCity]);
    const impressionsTotal = campaigns.reduce((sum, campaign) => sum + (campaign.impressions || 0), 0);
    const clicksTotal = campaigns.reduce((sum, campaign) => sum + (campaign.clicks || 0), 0);
    const totalCurrency = campaigns.find((campaign) => campaign.currency)?.currency || 'INR';
    const cpcTotal = clicksTotal > 0 ? spendTotal / clicksTotal : null;
    const achievedRate = targetsSet.length && targetTotal > 0 ? Math.round((achievedTotal / targetTotal) * 100) : null;
    const allCitiesScope = {
        campaignIds: campaigns.map((campaign) => campaign.campaignId).filter(Boolean).join(','),
    };
    const adsetsRequestedRef = useRef(new Set());
    const campaignIdsKey = React.useMemo(
        () => campaigns.map((campaign) => campaign.campaignId).filter(Boolean).sort().join(','),
        [campaigns],
    );

    useEffect(() => {
        adsetsRequestedRef.current = new Set();
        setAdsets({});
    }, [from, to]);

    useEffect(() => {
        if (managesCityDaily) {
            setCityDaily(cityDailyData);
            setDailyLoading(cityDailyLoading);
        }
    }, [managesCityDaily, cityDailyData, cityDailyLoading]);

    useEffect(() => {
        if (managesCityDaily) return undefined;
        const controller = new AbortController();
        if (!from || !to || loading || !campaignIdsKey) {
            setCityDaily(null);
            return () => controller.abort();
        }
        setDailyLoading(true);
        getCityDailyBreakdown({
            from,
            to,
            campaignIds: campaignIdsKey,
            platform: metaPlatform,
            signal: controller.signal,
        })
            .then((data) => {
                if (!controller.signal.aborted) setCityDaily(data);
            })
            .catch((error) => {
                if (isCanceled(error) || controller.signal.aborted) return;
                setCityDaily(null);
            })
            .finally(() => {
                if (!controller.signal.aborted) setDailyLoading(false);
            });
        return () => controller.abort();
    }, [from, to, campaignIdsKey, loading, metaPlatform, managesCityDaily]);

    function jumpToTodayColumn() {
        const container = scrollRef.current;
        if (!container || !dates.includes(today)) return;
        const target = container.querySelector(`[data-day-page="${today}"]`);
        if (!target) return;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const delta = targetRect.left - containerRect.left - STICKY_TOTAL_WIDTH;
        container.scrollTo({ left: Math.max(0, container.scrollLeft + delta), behavior: 'smooth' });
    }

    const tableBusy = Boolean(loading || dailyLoading || syncing);
    const loaderPhase = syncing && !loading && !dailyLoading
        ? 'sync'
        : loading
            ? 'campaigns'
            : 'daily';

    async function fetchAdsetsSnapshot() {
        const snapshot = { ...adsets };
        await Promise.all(campaigns.map(async (campaign) => {
            const id = campaign.campaignId;
            if (!id || snapshot[id]?.rows?.length) return;
            try {
                const data = await getCampaignReport({
                    from, to, level: 'adset', campaignId: id, platform: metaPlatform,
                });
                snapshot[id] = {
                    loading: false,
                    error: '',
                    rows: normalizeLevelRows(data, 'adset'),
                };
            } catch (error) {
                if (isCanceled(error)) return;
                snapshot[id] = {
                    loading: false,
                    error: adsErrorMessage(error, 'Unable to load ad sets.'),
                    rows: snapshot[id]?.rows || [],
                };
            }
        }));
        return snapshot;
    }

    async function handleDownloadExcel(detailLevel = 'city') {
        if (!campaigns.length || !cityDaily || !dates.length) return;
        setExportMenuAnchor(null);
        setExportingExcel(true);
        try {
            const adsetsState = detailLevel === 'full'
                ? await fetchAdsetsSnapshot()
                : adsets;
            downloadCampaignSpendTableXlsx({
                from,
                to,
                scopeLabel: scopeLabel || 'All cities',
                dates,
                groups,
                cityDaily,
                cityTargets,
                adsetsState,
                detailLevel,
                helpers: {
                    campaignRowsForCity,
                    cityDayMetrics,
                    sumDayAcrossCities,
                    sumCitySalesWon,
                    metricsForCampaignInCity,
                    campaignDayMetricsInCity,
                    entityDayMetrics,
                    matchesCityScope,
                },
                footerTotals: groups.length > 1 ? {
                    spend: spendTotal,
                    impressions: impressionsTotal,
                    clicks: clicksTotal,
                    leadCount: leadsReceivedTotal,
                    sqlReal: sqlTotal,
                    salesTbc: tbcTotal,
                    totalReal: achievedTotal,
                    salesWon: salesWonTotal,
                    salesWonAmount: salesWonAmountTotal,
                    targetTotal: targetsSet.length ? targetTotal : null,
                } : null,
            });
        } finally {
            setExportingExcel(false);
        }
    }

    async function loadLevel(level, ids, setter, key) {
        setter((current) => ({
            ...current,
            [key]: { loading: true, error: '', rows: current[key]?.rows || [] },
        }));
        try {
            const data = await getCampaignReport({ from, to, level, platform: metaPlatform, ...ids });
            const rows = normalizeLevelRows(data, level);
            setter((current) => ({
                ...current,
                [key]: { loading: false, error: '', rows },
            }));
        } catch (error) {
            if (isCanceled(error)) return;
            setter((current) => ({
                ...current,
                [key]: {
                    loading: false,
                    error: adsErrorMessage(error, 'Unable to load this breakdown.'),
                    rows: [],
                },
            }));
        }
    }

    function toggleCampaign(campaign) {
        const nextOpen = !openCampaigns[campaign.campaignId];
        setOpenCampaigns((current) => ({ ...current, [campaign.campaignId]: nextOpen }));
        if (nextOpen && !adsets[campaign.campaignId]) {
            loadLevel('adset', { campaignId: campaign.campaignId }, setAdsets, campaign.campaignId);
        }
    }

    function toggleAdset(campaignId, adset) {
        const key = `${campaignId}:${adset.id}`;
        const nextOpen = !openAdsets[key];
        setOpenAdsets((current) => ({ ...current, [key]: nextOpen }));
        if (nextOpen && !ads[key]) {
            loadLevel('ad', { campaignId, adsetId: adset.id }, setAds, key);
        }
    }

    function preloadAdsetsForCity(cityCampaigns) {
        cityCampaigns.forEach((campaign) => {
            if (!campaign.campaignId || adsets[campaign.campaignId]) return;
            loadLevel('adset', { campaignId: campaign.campaignId }, setAdsets, campaign.campaignId);
        });
    }

    function toggleCity(cityName, cityCampaigns) {
        const nextOpen = !openCities[cityName];
        setOpenCities((current) => ({ ...current, [cityName]: nextOpen }));
        if (nextOpen && cityCampaigns?.length) {
            preloadAdsetsForCity(cityCampaigns);
        }
    }

    function openLeadDetails(bucket, scope, contextLabel, dayDate) {
        const rangeFrom = dayDate || from;
        const rangeTo = dayDate || to;
        setLeadDialog({
            open: true,
            title: `${BUCKET_LABELS[bucket]} · ${contextLabel}`,
            query: {
                from: rangeFrom,
                to: rangeTo,
                bucket,
                ...scope,
                ...(metaPlatform && metaPlatform !== 'both' ? { platform: metaPlatform } : {}),
            },
        });
    }

    function closeLeadDialog() {
        setLeadDialog({ open: false, title: '', query: null });
    }

    function renderCampaignRows(cityRows, cityName) {
        const scopedCampaigns = cityRows.filter((campaign) => matchesCityScope(campaign, cityName));
        const cityScopeWithCampaigns = {
            city: cityName,
            ...(campaignIdsKey ? { campaignIds: campaignIdsKey } : {}),
        };
        return scopedCampaigns.map((campaign, index) => {
            const open = !!openCampaigns[campaign.campaignId];
            const detail = adsets[campaign.campaignId];
            const adsetRows = detail?.rows?.filter((adset) => matchesCityScope(adset, cityName)) || [];
            const cityCampaign = metricsForCampaignInCity(campaign, cityName, detail);
            const campaignLeadScope = { campaignId: campaign.campaignId, ...cityScopeWithCampaigns };
            return (
                <React.Fragment key={campaign.campaignId || index}>
                    <tr className={index % 2 ? classes.rowAlt : undefined}>
                        <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`}>
                            <IconButton
                                size="small"
                                aria-expanded={open}
                                aria-label={`Ad sets for ${campaign.campaignName}`}
                                onClick={() => toggleCampaign(campaign)}
                            >
                                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </td>
                        <NameCell
                            classes={classes}
                            name={campaign.campaignName}
                            id={campaign.campaignId}
                            cities={campaign.cities}
                            sticky
                        />
                        <MetricCells
                            row={cityCampaign}
                            classes={classes}
                            leadScope={campaignLeadScope}
                            contextLabel={`${campaign.campaignName} (${cityName})`}
                            onOpenLeads={openLeadDetails}
                            dates={dates}
                            today={today}
                            getDayMetrics={(date) => campaignDayMetricsInCity(
                                cityDaily,
                                campaign,
                                cityName,
                                detail,
                                date,
                            )}
                        />
                    </tr>
                    {open && detail?.loading && (
                        <tr className={classes.statusRow}>
                            <td className={classes.bodyCell} colSpan={columnCount}>
                                <CircularProgress size={18} />
                            </td>
                        </tr>
                    )}
                    {open && detail?.error && (
                        <tr className={classes.statusRow}>
                            <td className={classes.detailError} colSpan={columnCount} role="alert">
                                {detail.error}
                            </td>
                        </tr>
                    )}
                    {open && detail && !detail.loading && !detail.error && adsetRows.length === 0 && (
                        <tr className={classes.statusRow}>
                            <td className={classes.empty} colSpan={columnCount}>
                                {detail.rows?.length
                                    ? `No ad sets tagged to ${cityName} in this campaign.`
                                    : 'No ad set rows for this campaign.'}
                            </td>
                        </tr>
                    )}
                    {open && adsetRows.map((adset) => {
                        const adKey = `${campaign.campaignId}:${adset.id}`;
                        const adOpen = !!openAdsets[adKey];
                        const adDetail = ads[adKey];
                        const adRows = adDetail?.rows?.filter((ad) => matchesCityScope(ad, cityName)) || [];
                        return (
                            <React.Fragment key={adset.id}>
                                <tr className={classes.adsetRow}>
                                    <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`}>
                                        <IconButton
                                            size="small"
                                            aria-expanded={adOpen}
                                            aria-label={`Ads for ${adset.name}`}
                                            onClick={() => toggleAdset(campaign.campaignId, adset)}
                                        >
                                            {adOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </td>
                                    <NameCell
                                        classes={classes}
                                        label="Ad set"
                                        name={adset.name}
                                        id={adset.id}
                                        cities={adset.cities}
                                        sticky
                                    />
                                    <MetricCells
                                        row={adset}
                                        classes={classes}
                                        leadScope={{
                                            campaignId: campaign.campaignId,
                                            adsetId: adset.id,
                                            ...cityScopeWithCampaigns,
                                        }}
                                        contextLabel={adset.name}
                                        onOpenLeads={openLeadDetails}
                                        dates={dates}
                                        today={today}
                                        getDayMetrics={(date) => entityDayMetrics(cityDaily, 'adsets', adset.id, date)}
                                    />
                                </tr>
                                {adOpen && adDetail?.loading && (
                                    <tr className={classes.statusRow}>
                                        <td className={classes.bodyCell} colSpan={columnCount}>
                                            <CircularProgress size={18} />
                                        </td>
                                    </tr>
                                )}
                                {adOpen && adDetail?.error && (
                                    <tr className={classes.statusRow}>
                                        <td className={classes.detailError} colSpan={columnCount} role="alert">
                                            {adDetail.error}
                                        </td>
                                    </tr>
                                )}
                                {adOpen && adDetail && !adDetail.loading && !adDetail.error && adRows.length === 0 && (
                                    <tr className={classes.statusRow}>
                                        <td className={classes.empty} colSpan={columnCount}>
                                            {adDetail.rows?.length
                                                ? `No ads tagged to ${cityName} in this ad set.`
                                                : 'No ad rows for this ad set.'}
                                        </td>
                                    </tr>
                                )}
                                {adOpen && adRows.map((ad) => (
                                    <tr className={classes.adRow} key={ad.id}>
                                        <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`} />
                                        <NameCell
                                            classes={classes}
                                            label="Ad"
                                            tagClass={classes.adTag}
                                            name={ad.name}
                                            id={ad.id}
                                            cities={ad.cities}
                                            sticky
                                        />
                                        <MetricCells
                                            row={ad}
                                            classes={classes}
                                            leadScope={{
                                                campaignId: campaign.campaignId,
                                                adsetId: adset.id,
                                                adId: ad.id,
                                                ...cityScopeWithCampaigns,
                                            }}
                                            contextLabel={ad.name}
                                            onOpenLeads={openLeadDetails}
                                            dates={dates}
                                            today={today}
                                            getDayMetrics={(date) => entityDayMetrics(cityDaily, 'ads', ad.id, date)}
                                        />
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            );
        });
    }

    return (
        <>
        <section className={classes.card}>
            <div className={classes.heading}>
                <h2 className={classes.title}>Campaign breakdown</h2>
                <span className={classes.count}>
                    {scopeLabel || 'All cities'}
                    {' · '}
                    {campaigns.length} campaigns
                    {' · '}
                    Target {targetsSet.length ? formatNumber(targetTotal) : '—'}
                    {' · '}
                    Received {formatNumber(leadsReceivedTotal)}
                    {' · '}
                    Total Real {formatNumber(achievedTotal)}
                    {achievedRate != null ? ` · ${achievedRate}%` : ''}
                </span>
                <Button
                    className={classes.exportBtn}
                    variant="contained"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                    disabled={tableBusy || exportingExcel || !campaigns.length || !dates.length}
                >
                    {exportingExcel ? 'Preparing…' : 'Download Excel'}
                </Button>
                <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={() => handleDownloadExcel('city')}>
                        City summary only
                    </MenuItem>
                    <MenuItem onClick={() => handleDownloadExcel('full')}>
                        Full breakdown (campaigns + ad sets per city)
                    </MenuItem>
                </Menu>
            </div>
            {tableBusy ? (
                <MetaAdsCampaignLoader
                    active={tableBusy}
                    phase={loaderPhase}
                />
            ) : campaigns.length === 0 ? (
                <p className={classes.empty}>
                    {hasError
                        ? 'Campaign expenditure could not be loaded.'
                        : emptyMessage || 'No campaign data found for this date range. Try syncing or selecting another range.'}
                </p>
            ) : (
                <>
                <p className={classes.scrollHint}>
                    City names stay fixed; range totals appear next, then day columns (newest date first — scroll right for older days).
                    {dates.includes(today) ? (
                        <>
                            {' '}
                            Today (
                            {formatDayLabel(today)}
                            ) is highlighted.
                            <button type="button" className={classes.jumpTodayBtn} onClick={jumpToTodayColumn}>
                                Jump to today
                            </button>
                        </>
                    ) : null}
                </p>
                <div className={classes.tableScroll} ref={scrollRef}>
                    <table
                        className={classes.table}
                        style={{
                            minWidth: STICKY_TOTAL_WIDTH
                                + periodColumnCount * 96
                                + dates.length * METRICS_PER_DAY * 76,
                        }}
                    >
                        <colgroup>
                            <col style={{ width: STICKY_LEAD_WIDTH }} />
                            <col style={{ width: STICKY_CAMPAIGN_WIDTH }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 110 }} />
                            <col style={{ width: 84 }} />
                            <col style={{ width: 84 }} />
                            <col style={{ width: 80 }} />
                            <col style={{ width: 108 }} />
                            <col style={{ width: 84 }} />
                            <col style={{ width: 84 }} />
                            <col style={{ width: 96 }} />
                            <col style={{ width: 96 }} />
                            <col style={{ width: 84 }} />
                            <col style={{ width: 112 }} />
                            <col style={{ width: 112 }} />
                            {dates.map((date) => (
                                <React.Fragment key={`col-${date}`}>
                                    {Array.from({ length: METRICS_PER_DAY }).map((_, index) => (
                                        <col key={`${date}-${index}`} style={{ width: 76 }} />
                                    ))}
                                </React.Fragment>
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.stickyLead}`} rowSpan={2} aria-label="Expand" />
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.stickyNameHead}`} rowSpan={2}>Campaign</th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Spend</th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Impressions</th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Clicks</th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>CPC</th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Target</th>
                                <th
                                    className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`}
                                    rowSpan={2}
                                    title="All Meta form leads in the date range"
                                >
                                    Leads
                                    <br />
                                    received
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    SQL-
                                    <br />
                                    Real
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    Sales-
                                    <br />
                                    TBC
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    Total
                                    <br />
                                    Real
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    Sales
                                    <br />
                                    Won
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    Sales Won
                                    <br />
                                    value
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                    %
                                    <br />
                                    Achieved
                                </th>
                                <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>CPERP</th>
                                {dates.map((date, dateIndex) => (
                                    <th
                                        key={date}
                                        className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.datePageHead} ${dateIndex === 0 ? classes.datePageHeadSectionStart : ''} ${date === today ? classes.datePageHeadToday : ''}`}
                                        colSpan={METRICS_PER_DAY}
                                        data-day-page={date}
                                    >
                                        Date ·
                                        {' '}
                                        {formatDayLabel(date)}
                                        {date === today ? ' · Today' : ''}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {dates.map((date, dateIndex) => (
                                    <React.Fragment key={`sub-${date}`}>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead} ${dateIndex === 0 ? classes.datePageHeadSectionStart : ''}`}>Spend</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Leads</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>SQL</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>TBC</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Real</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Won</th>
                                        <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>CPERP</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map(([cityName, rows]) => {
                                const cityOpen = !!openCities[cityName];
                                const scopedCityRows = campaignRowsForCity(rows, cityName, adsets);
                                return (
                                    <React.Fragment key={cityName}>
                                        <CityTotalRow
                                            classes={classes}
                                            cityName={cityName}
                                            rows={scopedCityRows}
                                            savedTarget={cityTargets[cityName]}
                                            onSaveTarget={onSaveTarget}
                                            open={cityOpen}
                                            onToggle={() => toggleCity(cityName, rows)}
                                            onOpenLeads={openLeadDetails}
                                            dates={dates}
                                            cityDaily={cityDaily}
                                            today={today}
                                            reportCampaignIds={campaignIdsKey}
                                        />
                                        {cityOpen ? renderCampaignRows(rows, cityName) : null}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        {groups.length > 1 ? (
                            <tfoot>
                                <tr className={classes.totalRow}>
                                    <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`} />
                                    <td className={`${classes.bodyCell} ${classes.stickyNameBody}`}>All cities total</td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {formatMoney(spendTotal, totalCurrency)}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {formatNumber(impressionsTotal)}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {formatNumber(clicksTotal)}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {formatMetric(cpcTotal, (value) => formatMoney(value, totalCurrency))}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {targetsSet.length ? formatNumber(targetTotal) : '—'}
                                    </td>
                                    <LeadCountCell
                                        classes={classes}
                                        count={leadsReceivedTotal}
                                        bucket="received"
                                        scope={allCitiesScope}
                                        contextLabel="All cities"
                                        onOpenLeads={openLeadDetails}
                                    />
                                    <LeadCountCell
                                        classes={classes}
                                        count={sqlTotal}
                                        bucket="sql"
                                        scope={allCitiesScope}
                                        contextLabel="All cities"
                                        onOpenLeads={openLeadDetails}
                                    />
                                    <LeadCountCell
                                        classes={classes}
                                        count={tbcTotal}
                                        bucket="tbc"
                                        scope={allCitiesScope}
                                        contextLabel="All cities"
                                        onOpenLeads={openLeadDetails}
                                    />
                                    <LeadCountCell
                                        classes={classes}
                                        count={achievedTotal}
                                        bucket="real"
                                        scope={allCitiesScope}
                                        contextLabel="All cities"
                                        onOpenLeads={openLeadDetails}
                                    />
                                    <LeadCountCell
                                        classes={classes}
                                        count={salesWonTotal}
                                        bucket="won"
                                        scope={allCitiesScope}
                                        contextLabel="All cities"
                                        onOpenLeads={openLeadDetails}
                                    />
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {formatMoney(salesWonAmountTotal, totalCurrency)}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell} ${percentClass(classes, achievedRate)}`}>
                                        {achievedRate == null ? '—' : `${achievedRate}%`}
                                    </td>
                                    <td className={`${classes.bodyCell} ${classes.numCell}`}>
                                        {costPerReal(spendTotal, achievedTotal, totalCurrency)}
                                    </td>
                                    {dates.map((date, dateIndex) => (
                                        <DayPageCells
                                            key={`all-${date}`}
                                            classes={classes}
                                            day={sumDayAcrossCities(cityDaily, date)}
                                            isToday={date === today}
                                            isFirstDateColumn={dateIndex === 0}
                                            date={date}
                                            leadScope={allCitiesScope}
                                            contextLabel="All cities"
                                            onOpenLeads={openLeadDetails}
                                        />
                                    ))}
                                </tr>
                            </tfoot>
                        ) : null}
                    </table>
                </div>
                </>
            )}
        </section>
        <MetaAdsLeadDialog
            open={leadDialog.open}
            title={leadDialog.title}
            query={leadDialog.query}
            onClose={closeLeadDialog}
        />
        </>
    );
}
